import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, Mail, Phone, Lock, Sparkles, MapPin, ArrowRight } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AccessibilityBar } from './AccessibilityBar';
import { DEFAULT_SITE_LOGO, fetchSiteLogo } from '../lib/storage';

export function BrandLogo({ 
  variant = 'header',
  className = '' 
}: { 
  variant?: 'header' | 'footer';
  className?: string;
}) {
  const [hasImageError, setHasImageError] = useState(false);
  const [logoSrc, setLogoSrc] = useState(DEFAULT_SITE_LOGO);
  useEffect(() => {
    let cancelled = false;
    const applyLogo = (url?: string) => {
      const next = (url || '').trim();
      if (!next || next === DEFAULT_SITE_LOGO) {
        setHasImageError(false);
        setLogoSrc(DEFAULT_SITE_LOGO);
        return;
      }
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        setHasImageError(false);
        setLogoSrc(next);
      };
      probe.onerror = () => {
        if (cancelled) return;
        setHasImageError(false);
        setLogoSrc(DEFAULT_SITE_LOGO);
      };
      probe.src = next;
    };
    const handleLogoUpdate = (event: Event) => {
      applyLogo((event as CustomEvent<string>).detail);
    };
    window.addEventListener('site-logo-updated', handleLogoUpdate);
    fetchSiteLogo().then((url) => {
      if (!cancelled) applyLogo(url);
    });
    return () => {
      cancelled = true;
      window.removeEventListener('site-logo-updated', handleLogoUpdate);
    };
  }, []);

  const handleImageError = () => {
    if (logoSrc !== DEFAULT_SITE_LOGO) {
      setHasImageError(false);
      setLogoSrc(DEFAULT_SITE_LOGO);
      return;
    }
    setHasImageError(true);
  };

  if (variant === 'footer') {
    return (
      <div className={`flex items-center gap-3 text-white font-display font-bold text-xl ${className}`}>
        {!hasImageError ? (
          <img
            key={logoSrc}
            src={logoSrc}
            alt="Swaroop Web Solutions Logo"
            width={240}
            height={48}
            className="h-10 sm:h-12 w-auto max-w-[240px] object-contain rounded-lg"
            onError={handleImageError}
          />
        ) : (
          <span className="w-10 h-10 bg-[#0059bb] text-white rounded-lg flex items-center justify-center text-lg font-bold shadow-xs">
            S
          </span>
        )}
        <span>Swaroop Web Solutions</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 sm:gap-2.5 md:gap-3 min-w-0 ${className}`}>
      {!hasImageError ? (
        <img
          key={logoSrc}
          src={logoSrc}
          alt="Swaroop Web Solutions Logo"
          width={260}
          height={52}
          className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto max-w-[130px] sm:max-w-[160px] md:max-w-[190px] lg:max-w-[260px] object-contain shrink-0 group-hover:scale-105 transition-transform"
          onError={handleImageError}
        />
      ) : (
        <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-[#0059bb] rounded-xl flex items-center justify-center text-white font-bold text-base md:text-lg group-hover:scale-105 transition-transform shadow-sm shrink-0">
          S
        </div>
      )}
      <div className="flex flex-col justify-center min-w-0">
        <span className="font-display font-bold text-sm sm:text-base md:text-lg lg:text-xl tracking-tight text-[#0b1c30] leading-tight truncate">
          Swaroop <span className="text-[#0059bb]">Web Solutions</span>
        </span>
        <span className="text-[9px] sm:text-[10px] md:text-[11px] text-gray-600 font-medium tracking-wide truncate block">
          Web Engineering • AI-SEO • Performance
        </span>
      </div>
    </div>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <div className="sticky top-0 w-full z-50">
      <AccessibilityBar />
      <header className="w-full bg-white/95 backdrop-blur-md border-b border-gray-200/80 min-h-16 sm:min-h-20 h-auto shadow-xs flex items-center relative py-2 sm:py-0" role="banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-full w-full flex justify-between items-center gap-2 md:gap-4">
          <Link 
            to="/" 
            className="flex items-center group focus:ring-2 focus:ring-[#0059bb] rounded-lg p-1 outline-hidden shrink min-w-0"
            aria-label="Swaroop Web Solutions - Homepage"
          >
            <BrandLogo variant="header" />
          </Link>

          {/* Tablet & Desktop Nav - Active from 806px onwards */}
          <nav className="hidden md:flex items-center gap-3 lg:gap-6 xl:gap-8 shrink-0" role="navigation" aria-label="Main Navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-current={location.pathname === link.path ? 'page' : undefined}
                className={`font-sans font-semibold text-xs lg:text-sm transition-colors hover:text-[#0059bb] focus:ring-2 focus:ring-[#0059bb] rounded px-1.5 md:px-2 py-1 outline-hidden ${
                  location.pathname === link.path
                    ? 'text-[#0059bb] border-b-2 border-[#0059bb] pb-0.5'
                    : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-[#0b1c30] hover:bg-[#0059bb] text-white px-3 md:px-4 lg:px-5 py-2 lg:py-2.5 rounded-xl font-bold text-[11px] lg:text-xs uppercase tracking-wider transition-all shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-[#0b1c30] outline-hidden shrink-0"
            >
              Get Free Quote
            </Link>
          </nav>

          {/* Mobile Toggle - Active up to 805px */}
          <button 
            className="md:hidden p-2 text-gray-700 hover:text-[#0059bb] rounded-lg focus:ring-2 focus:ring-[#0059bb] outline-hidden min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0 cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation-menu"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile Navigation Menu Drawer - Active up to 805px */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-navigation-menu"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 w-full bg-white border-b border-gray-200 p-6 md:hidden flex flex-col gap-3 text-center shadow-2xl z-50 max-h-[calc(100vh-5rem)] overflow-y-auto"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-display font-bold text-lg py-2.5 rounded-xl transition-colors ${
                    location.pathname === link.path ? 'text-[#0059bb] bg-blue-50/60' : 'text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="bg-[#0059bb] text-white py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider shadow-sm mt-2"
              >
                Request Free Quote
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0b1c30] text-gray-200 border-t border-[#1e3a5f] pt-16 pb-12 font-sans" role="contentinfo">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1 space-y-4">
          <BrandLogo variant="footer" />
          <p className="text-gray-300 text-xs leading-relaxed">
            Engineering high-performance web applications, modern UI/UX design, technical SEO, and AI-SEO (Generative Engine Optimization) built for measurable business growth.
          </p>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2 text-gray-200">
              <Mail size={14} className="text-[#38bdf8] shrink-0" />
              <a href="mailto:care@swaroopwebsolutions.com" className="hover:text-white hover:underline focus:ring-1 focus:ring-blue-400 rounded">
                care@swaroopwebsolutions.com
              </a>
            </div>
            <div className="flex items-center gap-2 text-gray-200">
              <Phone size={14} className="text-[#4ade80] shrink-0" />
              <a href="tel:+919175931452" className="hover:text-white hover:underline focus:ring-1 focus:ring-blue-400 rounded">
                +91 91759 31452
              </a>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <MapPin size={14} className="text-amber-400 shrink-0" />
              <span>Pune, Maharashtra, India</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-display font-bold text-xs uppercase tracking-widest text-white mb-4">Core Services</h3>
          <ul className="space-y-2.5 text-xs text-gray-300">
            <li><Link to="/services" className="hover:text-white transition-colors">Website Design &amp; UI/UX</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">Website Development</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">SEO &amp; Generative Engine (GEO)</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">Social Media Marketing</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">Website AMC &amp; Hosting Support</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display font-bold text-xs uppercase tracking-widest text-white mb-4">Quick Navigation</h3>
          <ul className="space-y-2.5 text-xs text-gray-300">
            <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors">Our Services</Link></li>
            <li><Link to="/portfolio" className="hover:text-white transition-colors">Client Portfolio</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Get Free Proposal</Link></li>
          </ul>
        </div>

        <div className="space-y-3">
          <h3 className="font-display font-bold text-xs uppercase tracking-widest text-white mb-4">Speed &amp; Reliability</h3>
          <p className="text-xs text-gray-300 leading-relaxed">
            Every platform we engineer meets strict <strong>100/100 Core Web Vitals</strong> standards, sub-second response times, and WCAG AA accessibility.
          </p>
          <div className="p-3.5 bg-[#132742] rounded-xl border border-[#233e66] text-xs text-gray-200 space-y-1.5">
            <div className="font-bold text-white flex items-center gap-1.5">
              <Sparkles size={14} className="text-emerald-400" />
              <span>Direct Engineering Support</span>
            </div>
            <p className="text-gray-300 text-[11px] leading-relaxed">Direct consultations via <code>care@swaroopwebsolutions.com</code> or WhatsApp at <code>+91 91759 31452</code>.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-400">
        <span>© {new Date().getFullYear()} Swaroop Web Solutions. All rights reserved.</span>
        <div className="flex gap-6 text-xs items-center">
          <Link to="/terms" className="hover:text-white underline">Terms &amp; Conditions</Link>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppButton() {
  const waUrl = "https://api.whatsapp.com/send?phone=919175931452&text=" + encodeURIComponent("Hi Swaroop Web Solutions, I would like to inquire about your web development and AI-SEO services.");

  return (
    <a
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#075e2f] text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all group focus:ring-4 focus:ring-[#075e2f]/40 outline-hidden"
      aria-label="Chat directly with Swaroop Web Solutions on WhatsApp (+91 91759 31452)"
    >
      <MessageCircle size={30} />
      <span className="absolute right-full mr-3 bg-[#0b1c30] text-white px-3 py-1.5 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-bold uppercase tracking-wider shadow-lg">
        WhatsApp: +91 91759 31452
      </span>
    </a>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f9ff]">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-[#0b1c30] focus:px-4 focus:py-3 focus:text-white focus:rounded-lg">
        Skip to main content
      </a>
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-hidden">
        {children}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}